
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency, formatDate } from './utils';
import { DocumentIcon, ViewIcon, AlertIcon, SecurityIcon } from '../BorrowingsIcons';
import { toast } from "sonner";
import { RepaymentsTab } from './tabs/RepaymentsTab';
import { CovenantTab } from './tabs/CovenantTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { LoanEvents } from './LoanEvents';
import { CollateralManagement } from './CollateralManagement';
import { ScheduleChangeTab } from './tabs/ScheduleChangeTab';

interface LoanDetailsProps {
  currentLoan: any;
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  handleGenerateReport: () => void;
}

export const LoanDetails: React.FC<LoanDetailsProps> = ({
  currentLoan,
  selectedTab,
  setSelectedTab,
  handleGenerateReport
}) => {
  const [showInterestRateChange, setShowInterestRateChange] = useState(false);
  const [showPayoutAdjustment, setShowPayoutAdjustment] = useState(false);
  const [showRecalculatedSchedule, setShowRecalculatedSchedule] = useState(false);
  const [updatedRepayments, setUpdatedRepayments] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [pendingEvent, setPendingEvent] = useState<any>(null);

  if (!currentLoan) return null;
  
  // Handle schedule change save
  const handleScheduleChangeSave = (newRepayments: any[], event: any) => {
    setUpdatedRepayments(newRepayments);
    setPendingEvent(event);
    setShowRecalculatedSchedule(true);
    setShowInterestRateChange(false);
    setShowPayoutAdjustment(false);
  };

  // Handle schedule approval
  const handleApproveChanges = () => {
    // In a real app, this would update the database
    toast.success("Repayment schedule updated successfully");
    
    // Add the event to the events list
    const approvedEvent = { ...pendingEvent, status: 'Approved' };
    setEvents([...events, approvedEvent]);
    
    // Reset states
    setShowRecalculatedSchedule(false);
    setPendingEvent(null);
  };

  // Handle schedule rejection
  const handleRejectChanges = () => {
    toast.info("Changes discarded");
    setShowRecalculatedSchedule(false);
    setUpdatedRepayments([]);
    setPendingEvent(null);
  };

  // Handle approval of events
  const handleApproveEvent = (eventId: number) => {
    setEvents(events.map(event => 
      event.id === eventId 
        ? { ...event, status: 'Approved' } 
        : event
    ));
    toast.success("Event approved");
  };

  // Cancel buttons for schedule change forms
  const handleCancelScheduleChange = () => {
    setShowInterestRateChange(false);
    setShowPayoutAdjustment(false);
  };
  
  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">{currentLoan.instrumentType}</h2>
            <p className="text-muted-foreground">
              {currentLoan.lender} • {formatCurrency(currentLoan.amount, currentLoan.currency)} • 
              {currentLoan.interestRate}% {currentLoan.interestType}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={currentLoan.status === 'Active' ? 'default' : 'secondary'}>
                {currentLoan.status}
              </Badge>
              <span className="text-sm">
                Sanctioned: {formatDate(currentLoan.sanctionDate)} • Matures: {formatDate(currentLoan.maturityDate)}
              </span>
            </div>
          </div>
          <Button onClick={handleGenerateReport}>Generate Report</Button>
        </div>
        
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="grid grid-cols-6 mb-4">
            <TabsTrigger value="repayments">Repayments</TabsTrigger>
            <TabsTrigger value="security">
              <div className="flex items-center gap-1">
                <SecurityIcon className="h-4 w-4" />
                <span>Security</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="covenants">Covenants</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="schedule-changes">Schedule Changes</TabsTrigger>
          </TabsList>
          
          {/* Repayments Tab */}
          <TabsContent value="repayments" className="space-y-6">
            <RepaymentsTab repayments={currentLoan.repayments} />
          </TabsContent>
          
          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <CollateralManagement loan={currentLoan} />
          </TabsContent>
          
          {/* Covenants Tab */}
          <TabsContent value="covenants" className="space-y-6">
            <CovenantTab covenants={currentLoan.covenants} />
          </TabsContent>
          
          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <DocumentsTab documents={currentLoan.documents} />
          </TabsContent>
          
          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <LoanEvents events={events} onApprove={handleApproveEvent} />
          </TabsContent>
          
          {/* Schedule Changes Tab */}
          <TabsContent value="schedule-changes" className="space-y-6">
            <ScheduleChangeTab
              loan={currentLoan}
              showRecalculatedSchedule={showRecalculatedSchedule}
              showInterestRateChange={showInterestRateChange}
              showPayoutAdjustment={showPayoutAdjustment}
              updatedRepayments={updatedRepayments}
              handleScheduleChangeSave={handleScheduleChangeSave}
              handleApproveChanges={handleApproveChanges}
              handleRejectChanges={handleRejectChanges}
              handleCancelScheduleChange={handleCancelScheduleChange}
              setShowInterestRateChange={setShowInterestRateChange}
              setShowPayoutAdjustment={setShowPayoutAdjustment}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
